'use client';

import React, {useState} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BrainstormIdeaDialog,
  BugDialog,
  PrototypeWalkthroughDialog,
  QuickActionCard,
  QuickActionKey,
  QuickWinDialog,
  StartNewProjectDialog,
  quickActionDefs,
} from "@/app/components/home/HomeQuickActions";
import {cn} from "@/lib/utils";
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags";
import {ScopingIntakeDialog} from "@/app/components/scoping/ScopingIntakeDialog";
import {useCompanyId} from "@/hooks/use-company-id";

interface QuickLaunchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickLaunchModal({open, onOpenChange}: QuickLaunchModalProps) {
  const [activeAction, setActiveAction] = useState<QuickActionKey | null>(null);
  const {companyId} = useCompanyId();
  const bugsEnabled = useCompanyFeatureFlag(ff => ff.wsBugs);
  const createProjectsEnabled = useCompanyFeatureFlag(ff => ff.wsCreateProjects);

  const handleSelect = (action: QuickActionKey) => {
    // Close the wrapper modal so only the creation dialog is visible.
    onOpenChange(false);
    setActiveAction(action);
  };

  const handleDialogOpenChange = (action: QuickActionKey) => (next: boolean) => {
    if (!next) {
      setActiveAction(prev => (prev === action ? null : prev));
    }
  };

  const brainstormOpen = activeAction === "brainstorm";
  const quickWinOpen = activeAction === "quickWin";
  const bugOpen = activeAction === "bug";
  const projectOpen = activeAction === "project";
  const prototypeOpen = activeAction === "prototype";
  const scopeMultiOpen = activeAction === "scopeMulti";

  const visibleDefs = quickActionDefs.filter(def =>
    def.key !== "projectIntake" &&
    (!def.requiresBugs || bugsEnabled) &&
    (!def.requiresCreateProjects || createProjectsEnabled)
  );
  const gridCols = visibleDefs.length >= 4
    ? "md:grid-cols-2 lg:grid-cols-4"
    : "md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Start something new</DialogTitle>
            <DialogDescription>
              Brainstorm ideas, ship a quick feature, launch a full project, or analyze a prototype.
            </DialogDescription>
          </DialogHeader>
          <div className={cn("mt-4 grid gap-4", gridCols)}>
            {visibleDefs.map(({key, Icon, iconClassName, title, description}) => (
                <QuickActionCard
                  key={key}
                  icon={<Icon className={iconClassName}/>}
                  title={title}
                  description={description}
                  onClick={() => handleSelect(key)}
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <BrainstormIdeaDialog
        open={brainstormOpen}
        onOpenChange={handleDialogOpenChange("brainstorm")}
      />
      <QuickWinDialog
        open={quickWinOpen}
        onOpenChange={handleDialogOpenChange("quickWin")}
      />
      {bugsEnabled && (
        <BugDialog
          open={bugOpen}
          onOpenChange={handleDialogOpenChange("bug")}
        />
      )}
      <StartNewProjectDialog
        open={projectOpen}
        onOpenChange={handleDialogOpenChange("project")}
      />
      <PrototypeWalkthroughDialog
        open={prototypeOpen}
        onOpenChange={handleDialogOpenChange("prototype")}
      />
      <ScopingIntakeDialog
        open={scopeMultiOpen}
        onOpenChange={handleDialogOpenChange("scopeMulti")}
        companyId={companyId}
      />
    </>
  );
}
